import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import ConversationList from './components/ConversationList';
import MessagePanel from './components/MessagePanel';
import AnnouncementModal from './components/AnnouncementModal';
import GroupChatModal from './components/GroupChatModal';
import Button from '@/components/ui/Button';
import LoadingState from '@/components/ui/LoadingState';
import { useAuth } from '@/context/AuthContext';
import useTranslation from '@/hooks/useTranslation';
import { useLocations } from '@/context/LocationsContext';
import useNotifications from '@/hooks/useNotifications';
import { fetchEmployees as fetchEmployeesFromApi } from '@/lib/api/employees';
import { fetchUsers } from '@/lib/api/users';
import {
  fetchConversations,
  fetchConversationMessages,
  createConversation,
  sendMessage,
  markConversationSeen,
  uploadMessageAttachment,
  deleteConversation,
} from '@/lib/api/messaging';
import { normalizeConversation, normalizeMessage } from '@/lib/transformers';
import authStorage from '@/lib/authStorage';
import { API_BASE_URL } from '@/lib/apiClient';
import {
  applyMarkSeenOptimistic,
  applyRealtimeMessageSeen,
} from './utils/messageSeen';
import { NotificationSection } from 'shared';

const deriveSocketUrl = () => {
  try {
    const apiUrl = new URL(API_BASE_URL);
    return apiUrl.origin;
  } catch (error) {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return API_BASE_URL.replace(/\/api\/?$/, '');
  }
};

const normalizeIdentifier = (value) => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }
    return String(value);
  }

  if (typeof value === 'object') {
    const directIdentifier =
      value?.id
      ?? value?._id
      ?? value?.userId
      ?? value?.user_id
      ?? value?.sub
      ?? null;

    if (directIdentifier != null) {
      return normalizeIdentifier(directIdentifier);
    }

    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return stringified;
      }
    }
  }

  return null;
};

const collectStringCandidates = (...values) => {
  const collection = new Set();
  values.forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (typeof entry === 'string') {
          const trimmed = entry.trim();
          if (trimmed) {
            collection.add(trimmed);
          }
        }
      });
      return;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        collection.add(trimmed);
      }
      return;
    }

    if (value && typeof value === 'object') {
      const entries = [
        value?.name,
        value?.fullName,
        value?.displayName,
        value?.preferredName,
        value?.firstName && value?.lastName
          ? `${value.firstName} ${value.lastName}`
          : null,
      ];
      entries.forEach((entry) => {
        if (typeof entry === 'string') {
          const trimmed = entry.trim();
          if (trimmed) {
            collection.add(trimmed);
          }
        }
      });
    }
  });
  return Array.from(collection);
};

const collectSearchTerms = (...values) => {
  const terms = new Set();

  const visit = (value) => {
    if (!value) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        terms.add(trimmed);
      }
      return;
    }

    if (typeof value === 'object') {
      visit(value.name);
      visit(value.fullName);
      visit(value.displayName);
      visit(value.preferredName);
      visit(value.email);
      visit(value.department);
      visit(value.role);
      visit(value.firstName);
      visit(value.lastName);

      const combinedName = [value.firstName, value.lastName]
        .map((part) => (typeof part === 'string' ? part.trim() : ''))
        .filter(Boolean)
        .join(' ');
      if (combinedName) {
        visit(combinedName);
      }
    }
  };

  values.forEach(visit);
  return Array.from(terms);
};

const InternalMessaging = () => {
  const { t } = useTranslation(['header', 'common', 'internalMessaging']);
  const translate = useCallback(
    (key, fallback) => t(`internalMessaging:${key}`, fallback),
    [t],
  );
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    locations,
    loading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
  } = useLocations();
  const {
    notifications: chatNotifications,
    markAsSeen: markNotificationAsSeen,
    ready: notificationsReady,
  } = useNotifications(10000);
  const userRole = user?.role ?? 'manager';

  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showGroupChatModal, setShowGroupChatModal] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [directMessageQuery, setDirectMessageQuery] = useState('');
  const [isCreatingDirectConversation, setIsCreatingDirectConversation] =
    useState(false);
  const [directMessageError, setDirectMessageError] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isConversationListVisible, setIsConversationListVisible] =
    useState(true);

  const [rawConversations, setRawConversations] = useState([]);
  const [messagesByConversation, setMessagesByConversation] = useState({});
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [employeeSearchResults, setEmployeeSearchResults] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [employeeSearchError, setEmployeeSearchError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const latestUserLookupRef = useRef({});
  const pendingMarkSeenRef = useRef(new Set());
  const markSeenTimeoutRef = useRef(null);
  const deletedConversationIdsRef = useRef(new Set());
  const employeeSearchAbortRef = useRef(null);
  const employeeSearchDebounceRef = useRef(null);

  const currentUserIdentifiers = useMemo(() => {
    const identifiers = new Set();

    const register = (value) => {
      const normalized = normalizeIdentifier(value);
      if (normalized) {
        identifiers.add(normalized);
      }
    };

    register(user?.sub);
    register(user?.id);
    register(user?.userId);
    register(user?.user_id);
    register(user?._id);
    register(user?.employeeId);
    register(user?.employee_id);

    const profile = user?.profile ?? user?.currentEmployee ?? user?.employee;
    if (profile && typeof profile === 'object') {
      register(profile?.id);
      register(profile?._id);
      register(profile?.userId);
      register(profile?.user_id);
      register(profile?.employeeId);
      register(profile?.employee_id);
    }

    if (Array.isArray(user?.identities)) {
      user.identities.forEach((identity) => {
        register(identity?.id);
        register(identity?._id);
        register(identity?.userId);
        register(identity?.user_id);
        register(identity?.sub);
      });
    }

    return Array.from(identifiers);
  }, [user]);

  const currentUserId = currentUserIdentifiers[0] ?? null;

  const resolveParticipantId = useCallback((participant) => {
    if (!participant) {
      return null;
    }

    if (typeof participant === 'string' || typeof participant === 'number') {
      return String(participant);
    }

    if (typeof participant === 'object') {
      return participant?.id ?? participant?._id ?? participant?.userId ?? null;
    }

    return null;
  }, []);

  const fetchEmployeesSafely = useCallback(async () => {
    try {
      return await fetchEmployeesFromApi();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        return [];
      }
      throw err;
    }
  }, []);

  const fetchAllData = useCallback(
    async (controller) => {
      setLoading(true);
      setError(null);

      try {
        const requests = [fetchEmployeesSafely(), fetchConversations()];
        const shouldFetchUsers = userRole === 'manager' || userRole === 'admin';
        if (shouldFetchUsers) {
          requests.push(fetchUsers());
        }

        const results = await Promise.all(requests);
        if (!controller.current) {
          return;
        }

        const employeesData = results[0];
        const conversationsData = results[1];
        const usersData = shouldFetchUsers ? results[2] ?? [] : [];

        setEmployees(employeesData);
        setUsers(usersData);
        setRawConversations(
          conversationsData.map((conversation) => ({
            ...conversation,
            id:
              conversation?.id ??
              conversation?._id?.toString?.() ??
              conversation?._id,
          }))
        );
      } catch (err) {
        if (controller.current) {
          setError(err);
        }
      } finally {
        if (controller.current) {
          setLoading(false);
        }
      }
    },
    [fetchEmployeesSafely, userRole]
  );

  useEffect(() => {
    const controller = { current: true };
    fetchAllData(controller);
    return () => {
      controller.current = false;
    };
  }, [fetchAllData]);

  useEffect(() => {
    const normalizedQuery = (directMessageQuery ?? '').trim();
    if (!normalizedQuery) {
      setEmployeeSearchResults([]);
      setEmployeeSearchError(null);
      setEmployeeSearchLoading(false);
      if (employeeSearchAbortRef.current) {
        employeeSearchAbortRef.current.abort();
        employeeSearchAbortRef.current = null;
      }
      if (employeeSearchDebounceRef.current) {
        clearTimeout(employeeSearchDebounceRef.current);
        employeeSearchDebounceRef.current = null;
      }
      return undefined;
    }

    if (employeeSearchDebounceRef.current) {
      clearTimeout(employeeSearchDebounceRef.current);
    }

    employeeSearchDebounceRef.current = setTimeout(() => {
      employeeSearchDebounceRef.current = null;
      const controller = new AbortController();
      if (employeeSearchAbortRef.current) {
        employeeSearchAbortRef.current.abort();
      }
      employeeSearchAbortRef.current = controller;

      setEmployeeSearchLoading(true);
      setEmployeeSearchError(null);

      fetchEmployeesFromApi({
        params: { search: normalizedQuery },
        signal: controller.signal,
      })
        .then((results) => {
          if (controller.signal.aborted) {
            return;
          }
          setEmployeeSearchResults(results ?? []);
        })
        .catch((err) => {
          if (controller.signal.aborted) {
            return;
          }
          const message =
            err?.response?.data?.message ??
            err?.message ??
            translate('conversationList.dmSearchError', 'Unable to search employees');
          setEmployeeSearchError(message);
          setEmployeeSearchResults([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setEmployeeSearchLoading(false);
          }
          if (employeeSearchAbortRef.current === controller) {
            employeeSearchAbortRef.current = null;
          }
        });
    }, 275);

    return () => {
      if (employeeSearchDebounceRef.current) {
        clearTimeout(employeeSearchDebounceRef.current);
        employeeSearchDebounceRef.current = null;
      }
      if (employeeSearchAbortRef.current) {
        employeeSearchAbortRef.current.abort();
        employeeSearchAbortRef.current = null;
      }
    };
  }, [directMessageQuery, translate]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobileView) {
      setIsConversationListVisible(true);
      return;
    }

    if (!selectedConversationId) {
      setIsConversationListVisible(true);
    }
  }, [isMobileView, selectedConversationId]);

  const userLookup = useMemo(() => {
    const lookup = {};

    const setLookupEntry = (
      userId,
      {
        name = null,
        email = null,
        avatarFileId = null,
        avatarUrl = null,
        phone = null,
      } = {}
    ) => {
      if (!userId) {
        return;
      }

      const ensureString = (value) =>
        typeof value === 'string' && value.trim().length > 0 ? value : null;

      const existing = lookup[userId] ?? {};
      lookup[userId] = {
        name:
          ensureString(existing.name) ??
          ensureString(name) ??
          ensureString(email) ??
          `User ${String(userId).slice(-4)}`,
        email: ensureString(existing.email) ?? ensureString(email),
        avatarFileId:
          ensureString(existing.avatarFileId) ?? ensureString(avatarFileId),
        avatarUrl: ensureString(existing.avatarUrl) ?? ensureString(avatarUrl),
        phone: ensureString(existing.phone) ?? ensureString(phone),
      };
    };

    employees.forEach((employee) => {
      const userRef = employee?.raw?.user;
      const userId =
        typeof userRef === 'object' ? userRef?.id ?? userRef?._id : userRef;

      setLookupEntry(userId, {
        name: employee?.name,
        email: employee?.email,
        avatarFileId: employee?.avatarFileId,
        avatarUrl: employee?.avatarUrl,
        phone:
          employee?.phone ??
          employee?.raw?.phone ??
          employee?.contactPhone ??
          null,
      });
    });

    users.forEach((userDoc) => {
      const userId = userDoc?.id ?? userDoc?._id;
      setLookupEntry(userId, {
        name: userDoc?.fullName ?? userDoc?.email,
        email: userDoc?.email,
        avatarUrl: userDoc?.avatarUrl,
        phone: userDoc?.phone ?? userDoc?.phoneNumber ?? null,
      });
    });

    rawConversations.forEach((conversation) => {
      (conversation?.participants ?? []).forEach((participant) => {
        const participantObject =
          participant && typeof participant === 'object' ? participant : {};
        const userId =
          participantObject?.id ??
          participantObject?._id ??
          (typeof participant === 'string' ? participant : null);

        setLookupEntry(userId, {
          name:
            participantObject?.fullName ??
            participantObject?.name ??
            participantObject?.email,
          email: participantObject?.email,
          avatarFileId: participantObject?.avatarFileId,
          avatarUrl: participantObject?.avatarUrl ?? participantObject?.avatar,
          phone:
            participantObject?.phone ??
            participantObject?.phoneNumber ??
            participantObject?.contactPhone ??
            null,
        });
      });
    });

    if (user?.sub) {
      setLookupEntry(user.sub, {
        name: user?.fullName ?? user?.email ?? translate('defaults.you', 'You'),
        email: user?.email,
        avatarUrl: user?.avatarUrl,
        phone: user?.phone ?? user?.phoneNumber ?? null,
      });
    }

    return lookup;
  }, [employees, users, user, rawConversations]);

  useEffect(() => {
    latestUserLookupRef.current = userLookup;
  }, [userLookup]);

  const handleRealtimeEvent = useCallback(
    (payload) => {
      if (!payload || typeof payload !== 'object') {
        return;
      }

      const eventType = payload.type ?? 'message:new';

      if (eventType === 'message:new') {
        const conversationPayload = payload?.conversation ?? {};
        const conversationId =
          payload?.conversationId ??
          conversationPayload?.id ??
          conversationPayload?._id ??
          null;
        const messagePayload = payload?.message ?? null;

        if (!conversationId || !messagePayload) {
          return;
        }

        if (deletedConversationIdsRef.current.has(conversationId)) {
          return;
        }

        const normalizedConversation = {
          ...conversationPayload,
          id:
            conversationPayload?.id ??
            conversationPayload?._id ??
            conversationId,
        };

        const timestampSource =
          messagePayload?.createdAt ??
          messagePayload?.timestamp ??
          normalizedConversation?.lastMessageAt ??
          new Date().toISOString();

        const resolvedTimestamp = (() => {
          if (timestampSource instanceof Date) {
            return timestampSource;
          }
          const date = new Date(timestampSource);
          return Number.isNaN(date.getTime()) ? new Date() : date;
        })();

        const normalizedSenderId = payload?.senderId
          ? String(payload.senderId)
          : normalizeMessage(
              messagePayload,
              latestUserLookupRef.current,
              currentUserId
            )?.senderId ?? null;

        setRawConversations((prev) => {
          const withoutCurrent = prev.filter((conversation) => {
            const identifier = conversation?.id ?? conversation?._id;
            return identifier !== conversationId;
          });

          const existing = prev.find((conversation) => {
            const identifier = conversation?.id ?? conversation?._id;
            return identifier === conversationId;
          });

          const previousCount = (() => {
            const rawCount =
              existing?.unseenCount ??
              existing?.unreadCount ??
              normalizedConversation?.unseenCount ??
              0;
            const numeric = Number(rawCount);
            return Number.isFinite(numeric) && numeric >= 0
              ? Math.floor(numeric)
              : 0;
          })();

          const shouldIncrement = normalizedSenderId
            ? normalizedSenderId !== String(currentUserId ?? '')
            : false;

          const mergedUnseen = shouldIncrement ? previousCount + 1 : 0;

          const merged = {
            ...(existing ?? {}),
            ...normalizedConversation,
            id: conversationId,
            lastMessageAt: resolvedTimestamp.toISOString(),
            unseenCount: mergedUnseen,
            unreadCount: mergedUnseen,
          };

          return [merged, ...withoutCurrent];
        });

        setMessagesByConversation((prev) => {
          const existing = prev[conversationId] ?? [];
          const normalizedMessage = normalizeMessage(
            messagePayload,
            latestUserLookupRef.current,
            currentUserId
          );

          const fileSizeFromPayload =
            messagePayload?.fileSize ??
            (typeof messagePayload?.attachments === 'object' &&
            messagePayload?.attachments !== null &&
            typeof messagePayload.attachments.size === 'number'
              ? messagePayload.attachments.size
              : null);

          const decorated =
            normalizedMessage?.type === 'file' && fileSizeFromPayload != null
              ? { ...normalizedMessage, fileSize: fileSizeFromPayload }
              : normalizedMessage;

          if (existing.some((entry) => entry?.id === decorated?.id)) {
            return prev;
          }

          return {
            ...prev,
            [conversationId]: [...existing, decorated],
          };
        });

        return;
      }

      if (eventType === 'message:seen') {
        const conversationId =
          payload?.conversationId ??
          payload?.conversation?.id ??
          payload?.conversation?._id ??
          null;
        const messageIdsRaw = Array.isArray(payload?.messageIds)
          ? payload.messageIds
          : [];
        const actorUserId = payload?.userId ? String(payload.userId) : null;
        if (!conversationId || !messageIdsRaw.length || !actorUserId) {
          return;
        }

        if (deletedConversationIdsRef.current.has(conversationId)) {
          return;
        }

        const messageIds = messageIdsRaw.map((id) => String(id));
        const seenAtValue = payload?.seenAt ?? new Date().toISOString();
        const seenAtDate = (() => {
          if (seenAtValue instanceof Date) {
            return Number.isNaN(seenAtValue.getTime())
              ? new Date()
              : seenAtValue;
          }
          const parsed = new Date(seenAtValue);
          return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
        })();

        setMessagesByConversation((prev) => {
          const existing = prev[conversationId];
          if (!existing) {
            return prev;
          }

          const updated = applyRealtimeMessageSeen(
            existing,
            messageIds,
            actorUserId,
            currentUserId,
            seenAtDate
          );

          if (!updated || updated === existing) {
            return prev;
          }

          return {
            ...prev,
            [conversationId]: updated,
          };
        });

        if (actorUserId === String(currentUserId ?? '')) {
          setRawConversations((prev) =>
            prev.map((conversation) => {
              const identifier = conversation?.id ?? conversation?._id;
              if (identifier !== conversationId) {
                return conversation;
              }

              if (
                (conversation?.unseenCount ??
                  conversation?.unreadCount ??
                  0) === 0
              ) {
                return conversation;
              }

              return {
                ...conversation,
                unseenCount: 0,
                unreadCount: 0,
              };
            })
          );
        }
      }
    },
    [currentUserId]
  );

  const markConversationSeenRequest = useCallback(
    async (conversationId) => {
      if (!conversationId || !currentUserId) {
        return;
      }

      const normalizedConversationId = String(conversationId);

      const conversationMeta = rawConversations.find((conversation) => {
        const identifier = conversation?.id ?? conversation?._id;
        return identifier === normalizedConversationId;
      });

      if (conversationMeta?.isDraft) {
        return;
      }

      if (pendingMarkSeenRef.current.has(normalizedConversationId)) {
        return;
      }

      const messages = messagesByConversation[normalizedConversationId] ?? [];
      const hasUnseenMessages = messages.some(
        (message) =>
          message &&
          message.senderId !== String(currentUserId) &&
          !message.seenAt
      );

      if (!hasUnseenMessages) {
        setRawConversations((prev) =>
          prev.map((conversation) => {
            const identifier = conversation?.id ?? conversation?._id;
            if (identifier !== normalizedConversationId) {
              return conversation;
            }

            if (
              (conversation?.unseenCount ?? conversation?.unreadCount ?? 0) ===
              0
            ) {
              return conversation;
            }

            return {
              ...conversation,
              unseenCount: 0,
              unreadCount: 0,
            };
          })
        );
        return;
      }

      pendingMarkSeenRef.current.add(normalizedConversationId);

      try {
        const response = await markConversationSeen(normalizedConversationId);
        const messageIds = Array.isArray(response?.messageIds)
          ? response.messageIds.map((id) => String(id))
          : [];
        const seenAtValue = response?.seenAt ?? new Date().toISOString();
        const seenAtDate = (() => {
          if (seenAtValue instanceof Date) {
            return Number.isNaN(seenAtValue.getTime())
              ? new Date()
              : seenAtValue;
          }
          const parsed = new Date(seenAtValue);
          return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
        })();

        if (messageIds.length) {
          setMessagesByConversation((prev) => {
            const existing = prev[normalizedConversationId];
            if (!existing) {
              return prev;
            }

            const updated = applyMarkSeenOptimistic(
              existing,
              messageIds,
              currentUserId,
              seenAtDate
            );

            if (!updated || updated === existing) {
              return prev;
            }

            return {
              ...prev,
              [normalizedConversationId]: updated,
            };
          });
        }

        setRawConversations((prev) =>
          prev.map((conversation) => {
            const identifier = conversation?.id ?? conversation?._id;
            if (identifier !== normalizedConversationId) {
              return conversation;
            }

            if (
              (conversation?.unseenCount ?? conversation?.unreadCount ?? 0) ===
              0
            ) {
              return conversation;
            }

            return {
              ...conversation,
              unseenCount: 0,
              unreadCount: 0,
            };
          })
        );
      } catch (error) {
        console.error('Failed to mark conversation seen', error);
      } finally {
        pendingMarkSeenRef.current.delete(normalizedConversationId);
      }
    },
    [messagesByConversation, currentUserId, rawConversations]
  );

  const conversations = useMemo(
    () =>
      rawConversations.map((conversation) =>
        normalizeConversation(conversation, userLookup, currentUserIdentifiers)
      ),
    [rawConversations, userLookup, currentUserIdentifiers]
  );

  useEffect(() => {
    if (!conversations.length) {
      return;
    }

    if (!selectedConversationId) {
      setSelectedConversationId(conversations[0]?.id);
      return;
    }

    const stillExists = conversations.some(
      (conversation) => conversation?.id === selectedConversationId
    );
    if (!stillExists) {
      setSelectedConversationId(conversations[0]?.id);
    }
  }, [conversations, selectedConversationId]);

  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (conversation) => conversation?.id === selectedConversationId
      ) ?? null,
    [conversations, selectedConversationId]
  );

  const enhancedConversations = useMemo(
    () =>
      conversations.map((conversation) => {
        const history = messagesByConversation[conversation?.id];
        if (history?.length) {
          const lastMessage = history[history.length - 1];
          return {
            ...conversation,
            lastMessage:
              lastMessage?.type === 'file'
                ? `📎 ${lastMessage?.content}`
                : lastMessage?.content,
            lastSender: lastMessage?.senderName,
            lastMessageType: lastMessage?.type,
            timestamp: lastMessage?.timestamp,
          };
        }
        return {
          ...conversation,
          lastMessage: conversation?.lastMessage
            ?? translate('conversationList.emptyDefault', 'No messages yet'),
        };
      }),
    [conversations, messagesByConversation]
  );

  const conversationMessages = useMemo(
    () => messagesByConversation[selectedConversation?.id] ?? [],
    [messagesByConversation, selectedConversation?.id]
  );

  useEffect(() => {
    const conversationId = selectedConversation?.id;
    if (!conversationId || !notificationsReady) {
      return;
    }

    const normalizedConversationId = String(conversationId).trim();
    if (!normalizedConversationId) {
      return;
    }

    const extractConversationId = (notification) => {
      const metadataConversationId =
        notification?.metadata?.conversationId
        ?? notification?.metadata?.conversationID
        ?? notification?.metadata?.conversation?.id
        ?? notification?.metadata?.conversation;

      if (metadataConversationId != null) {
        const normalizedId = String(metadataConversationId).trim();
        if (normalizedId) {
          return normalizedId;
        }
      }

      const targetUrl = notification?.targetUrl;
      if (typeof targetUrl === 'string') {
        try {
          const parsedUrl = new URL(
            targetUrl,
            typeof window !== 'undefined' && window.location?.origin
              ? window.location.origin
              : 'http://localhost',
          );
          const targetConversationId = parsedUrl.searchParams
            ?.get('conversation')
            ?.trim();
          if (targetConversationId) {
            return targetConversationId;
          }
        } catch {
          // ignore parsing errors
        }
      }

      return null;
    };

    const relevantNotifications = (chatNotifications ?? []).filter((notification) => {
      if (!notification || notification.seen) {
        return false;
      }

      if (
        notification.section
        && notification.section !== NotificationSection.INTERNAL_CHAT
      ) {
        return false;
      }

      const targetConversationId = extractConversationId(notification);
      if (!targetConversationId) {
        return false;
      }

      return targetConversationId === normalizedConversationId;
    });

    relevantNotifications.forEach((notification) => {
      const notificationId = notification?.id ?? notification?._id;
      if (!notificationId) {
        return;
      }

      markNotificationAsSeen(notificationId).catch(() => {});
    });
  }, [
    chatNotifications,
    notificationsReady,
    selectedConversation?.id,
    markNotificationAsSeen,
  ]);

  useEffect(() => {
    const conversationId = selectedConversation?.id;
    if (!conversationId || !currentUserId) {
      return undefined;
    }

    const scheduleMarkSeen = () => {
      const hasUnseen = conversationMessages.some(
        (message) =>
          message &&
          message.senderId !== String(currentUserId) &&
          !message.seenAt
      );

      if (!hasUnseen) {
        return;
      }

      if (markSeenTimeoutRef.current) {
        clearTimeout(markSeenTimeoutRef.current);
      }

      markSeenTimeoutRef.current = setTimeout(() => {
        markConversationSeenRequest(conversationId).catch(() => {});
      }, 250);
    };

    scheduleMarkSeen();

    return () => {
      if (markSeenTimeoutRef.current) {
        clearTimeout(markSeenTimeoutRef.current);
        markSeenTimeoutRef.current = null;
      }
    };
  }, [
    selectedConversation?.id,
    conversationMessages,
    currentUserId,
    markConversationSeenRequest,
  ]);

  useEffect(() => {
    const conversationId = selectedConversation?.id;
    if (!conversationId || !currentUserId) {
      return undefined;
    }

    const handleFocus = () => {
      const messages = messagesByConversation[conversationId] ?? [];
      const hasUnseen = messages.some(
        (message) =>
          message &&
          message.senderId !== String(currentUserId) &&
          !message.seenAt
      );

      if (hasUnseen) {
        markConversationSeenRequest(conversationId).catch(() => {});
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [
    selectedConversation?.id,
    messagesByConversation,
    currentUserId,
    markConversationSeenRequest,
  ]);

  const currentUserProfile = useMemo(
    () => ({
      id: currentUserIdentifiers[0] ?? user?.sub ?? 'current-user',
      name:
        userLookup[user?.sub]?.name
          ?? user?.fullName
          ?? user?.email
          ?? translate('defaults.you', 'You'),
      role: userRole,
      avatarFileId: userLookup[user?.sub]?.avatarFileId ?? null,
      avatarUrl: userLookup[user?.sub]?.avatarUrl ?? user?.avatarUrl ?? null,
      identifiers: currentUserIdentifiers,
    }),
    [currentUserIdentifiers, user, userLookup, userRole]
  );

  const loadMessages = useCallback(
    async (conversationId) => {
      if (!conversationId || messagesByConversation[conversationId]) {
        return;
      }

      const conversationMeta = rawConversations.find((conversation) => {
        const identifier = conversation?.id ?? conversation?._id;
        return identifier === conversationId;
      });

      if (conversationMeta?.isDraft) {
        return;
      }
      try {
        const data = await fetchConversationMessages(conversationId);
        const normalized = data.map((message) =>
          normalizeMessage(message, userLookup, currentUserIdentifiers)
        );
        setMessagesByConversation((prev) => ({
          ...prev,
          [conversationId]: normalized,
        }));
      } catch (err) {
        setError(err);
      }
    },
    [
      messagesByConversation,
      rawConversations,
      userLookup,
      currentUserIdentifiers,
    ]
  );

  useEffect(() => {
    if (selectedConversation?.id) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation, loadMessages]);

  useEffect(() => {
    const token = authStorage.getAccessToken();
    if (!token || socketRef.current) {
      return undefined;
    }

    const baseUrl = new URL(deriveSocketUrl());
    baseUrl.protocol = baseUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    baseUrl.pathname = '/messaging';
    baseUrl.search = `token=${encodeURIComponent(token)}`;

    const socket = new WebSocket(baseUrl.toString());
    socketRef.current = socket;

    const handleMessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (!payload) {
          return;
        }
        if (payload.type === 'connection:ack') {
          return;
        }

        handleRealtimeEvent(payload);
      } catch (error) {
        console.error('Failed to parse messaging payload', error);
      }
    };

    const handleError = (event) => {
      console.error('Messaging websocket error', event);
    };

    socket.addEventListener('message', handleMessage);
    socket.addEventListener('error', handleError);

    return () => {
      socket.removeEventListener('message', handleMessage);
      socket.removeEventListener('error', handleError);
      socket.close();
      socketRef.current = null;
    };
  }, [handleRealtimeEvent, user?.sub]);

  useEffect(() => {
    const socket = socketRef.current;
    const conversationId = selectedConversation?.id;

    if (!socket || !conversationId) {
      return;
    }

    const sendJoin = () => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'join', conversationId }));
      }
    };

    if (socket.readyState === WebSocket.OPEN) {
      sendJoin();
      return;
    }

    const handleOpen = () => {
      sendJoin();
    };

    socket.addEventListener('open', handleOpen);
    return () => {
      socket.removeEventListener('open', handleOpen);
    };
  }, [selectedConversation?.id]);

  const handleSelectConversation = useCallback(
    (conversation) => {
      const conversationId = conversation?.id ?? null;
      setSelectedConversationId(conversationId);
      if (conversationId && isMobileView) {
        setIsConversationListVisible(false);
      }
    },
    [isMobileView]
  );

  const handleDeleteConversation = useCallback(
    async (conversation) => {
      const conversationId = conversation?.id ?? null;
      if (!conversationId) {
        return;
      }

      const confirmationMessage = translate(
        'messagePanel.actions.deleteConfirm',
        'Are you sure you want to delete this conversation? It will be hidden for you.',
      );
      if (typeof window !== 'undefined') {
        const confirmed = window.confirm(confirmationMessage);
        if (!confirmed) {
          return;
        }
      }

      deletedConversationIdsRef.current.add(conversationId);

      try {
        await deleteConversation(conversationId);

        setMessagesByConversation((prev) => {
          const { [conversationId]: _removed, ...rest } = prev;
          return rest;
        });

        let nextSelectedId = null;
        setRawConversations((prev) => {
          const filtered = prev.filter((item) => {
            const identifier = item?.id ?? item?._id;
            return identifier !== conversationId;
          });

          if (selectedConversationId === conversationId) {
            const nextConversation = filtered[0] ?? null;
            nextSelectedId =
              nextConversation?.id ?? nextConversation?._id ?? null;
          }

          return filtered;
        });

        if (selectedConversationId === conversationId) {
          setSelectedConversationId(nextSelectedId);
          if (isMobileView) {
            setIsConversationListVisible(true);
          }
        }
      } catch (error) {
        console.error('Failed to delete conversation', error);
        deletedConversationIdsRef.current.delete(conversationId);
      }
    },
    [isMobileView, selectedConversationId]
  );

  const handleBackToConversationList = useCallback(() => {
    setIsConversationListVisible(true);
  }, []);

  const handleSendMessage = useCallback(
    async (messageData) => {
      const isDraftConversation = Boolean(selectedConversation?.isDraft);

      if (!selectedConversation?.id) {
        throw new Error(
          translate('errors.send.noConversation', 'Select a conversation to continue.'),
        );
      }

      const payload = {
        conversationId: selectedConversation.id,
        type: messageData?.type ?? 'text',
      };

      const rawContent = messageData?.content ?? '';
      const normalizedContent =
        payload.type === 'text' ? rawContent.trim() : rawContent;

      if (!normalizedContent) {
        throw new Error(translate('errors.send.empty', 'Message cannot be empty'));
      }

      let attachmentUpload = null;

      if (payload.type === 'file') {
        const file = messageData?.file;
        const isFileInstance =
          typeof File !== 'undefined' && file instanceof File;

        if (!isFileInstance) {
          throw new Error(
            translate(
              'errors.send.attachmentMissing',
              'Select a file to upload before sending.',
            ),
          );
        }

        attachmentUpload = await uploadMessageAttachment(file);

        if (!attachmentUpload?.fileId) {
          throw new Error(
            translate(
              'errors.send.attachmentFailed',
              'Unable to upload attachment.',
            ),
          );
        }
      }

      const requestPayload = {
        ...payload,
        content: attachmentUpload?.originalName ?? normalizedContent,
      };

      if (attachmentUpload) {
        requestPayload.attachmentFileId = attachmentUpload.fileId;

        if (attachmentUpload.originalName) {
          requestPayload.attachmentName = attachmentUpload.originalName;
        }

        if (typeof attachmentUpload.size === 'number') {
          requestPayload.attachmentSize = attachmentUpload.size;
        }

        if (attachmentUpload.contentType) {
          requestPayload.attachmentContentType = attachmentUpload.contentType;
        }
      }

      const creationPayload = selectedConversation?.draftPayload ?? null;
      let targetConversationId = selectedConversation.id;
      let response = null;

      if (isDraftConversation && creationPayload) {
        const creationResponse = await createConversation({
          ...creationPayload,
          initialMessage: {
            type: requestPayload.type,
            content: requestPayload.content,
            attachmentFileId: requestPayload.attachmentFileId,
            attachmentName: requestPayload.attachmentName,
            attachmentSize: requestPayload.attachmentSize,
            attachmentContentType: requestPayload.attachmentContentType,
            attachmentUrl: requestPayload.attachmentUrl,
          },
        });

        const createdConversation =
          creationResponse?.conversation ?? creationResponse;
        const initialMessage = creationResponse?.initialMessage ?? null;

        const conversationId = createdConversation?.id ?? createdConversation?._id;
        if (!conversationId) {
          throw new Error(
            translate(
              'errors.conversation.createFailed',
              'Unable to create conversation',
            ),
          );
        }

        targetConversationId = conversationId;

        const normalizedConversation = {
          ...createdConversation,
          id: conversationId,
          lastMessageAt:
            createdConversation?.lastMessageAt ?? new Date().toISOString(),
        };

        setRawConversations((prev) => {
          const withoutDraft = prev.filter((existing) => {
            const identifier = existing?.id ?? existing?._id;
            return identifier !== selectedConversation.id;
          });

          return [normalizedConversation, ...withoutDraft];
        });

        const normalizedInitialMessage = initialMessage
          ? normalizeMessage(
            initialMessage,
            latestUserLookupRef.current,
            currentUserId,
          )
          : null;

        setMessagesByConversation((prev) => {
          const cleaned = { ...prev };
          delete cleaned[selectedConversation.id];
          const existingMessages = cleaned[conversationId] ?? [];
          const mergedMessages = normalizedInitialMessage
            ? [...existingMessages, normalizedInitialMessage]
            : existingMessages;

          return {
            ...cleaned,
            [conversationId]: mergedMessages,
          };
        });

        setSelectedConversationId(conversationId);
        response = initialMessage;
      } else {
        response = await sendMessage(requestPayload);
      }

      const normalized = response
        ? normalizeMessage(
          response,
          latestUserLookupRef.current,
          currentUserId,
        )
        : null;

      const decorated = (() => {
        if (normalized?.type !== 'file') {
          return normalized;
        }

        const rawSize = attachmentUpload?.size ?? messageData?.fileSize ?? null;
        const normalizedSize =
          typeof rawSize === 'number' && Number.isFinite(rawSize) && rawSize >= 0
            ? rawSize
            : null;

        if (normalizedSize !== null) {
          return { ...normalized, fileSize: normalizedSize };
        }

        return normalized;
      })();

      setMessagesByConversation((prev) => {
        const existing = prev[targetConversationId] ?? [];
        if (existing.some((item) => item?.id === decorated?.id)) {
          return prev;
        }

        const updated = {
          ...prev,
          [targetConversationId]: decorated ? [...existing, decorated] : existing,
        };

        if (isDraftConversation) {
          delete updated[selectedConversation.id];
        }

        return updated;
      });

      setRawConversations((prev) => {
        const updatedTimestamp =
          decorated?.timestamp instanceof Date
            ? decorated.timestamp.toISOString()
            : new Date(decorated?.timestamp ?? Date.now()).toISOString();

        const withoutCurrent = prev.filter((conversation) => {
          const identifier = conversation?.id ?? conversation?._id;
          return identifier !== targetConversationId;
        });

        const activeConversation = prev.find((conversation) => {
          const identifier = conversation?.id ?? conversation?._id;
          return identifier === targetConversationId;
        });

        return [
          {
            ...(activeConversation ?? selectedConversation ?? {}),
            id: targetConversationId,
            lastMessageAt: updatedTimestamp,
            unseenCount: 0,
            unreadCount: 0,
          },
          ...withoutCurrent,
        ];
      });
    },
    [
      selectedConversation,
      translate,
      uploadMessageAttachment,
      latestUserLookupRef,
      currentUserId,
      messagesByConversation,
      setMessagesByConversation,
      setRawConversations,
      setSelectedConversationId,
      sendMessage,
      createConversation,
    ],
  );

  const handleSendAnnouncement = useCallback(
    (announcementData) => {
      const announcementConversation = enhancedConversations.find(
        (conversation) => conversation?.type === 'announcement'
      );
      if (!announcementConversation) {
        return;
      }

      const announcementMessage = {
        id: Date.now(),
        senderId: currentUserProfile?.id,
        senderName: currentUserProfile?.name,
        senderAvatarFileId: currentUserProfile?.avatarFileId ?? null,
        senderAvatarUrl: currentUserProfile?.avatarUrl ?? null,
        type: 'announcement',
        content: `📢 ${announcementData?.title}\n\n${announcementData?.message}`,
        timestamp: new Date(announcementData?.timestamp ?? Date.now()),
        status: 'sent',
        priority: announcementData?.priority,
        locations: announcementData?.locations,
        departments: announcementData?.departments,
        requireReadReceipt: announcementData?.requireReadReceipt,
      };

      setMessagesByConversation((prev) => ({
        ...prev,
        [announcementConversation.id]: [
          ...(prev[announcementConversation.id] ?? []),
          announcementMessage,
        ],
      }));

      setRawConversations((prev) =>
        prev.map((conversation) => {
          const conversationId = conversation?.id ?? conversation?._id;
          if (conversationId === announcementConversation.id) {
            return {
              ...conversation,
              lastMessageAt: announcementMessage.timestamp.toISOString(),
            };
          }
          return conversation;
        })
      );
    },
    [currentUserProfile, enhancedConversations]
  );

  const handleCreateGroup = useCallback(async (groupData) => {
    const memberIds = (groupData?.members ?? [])
      .map((member) => member?.id ?? member)
      .filter(Boolean);

    if (!memberIds.length) {
      throw new Error(translate('errors.group.noMembers', 'Select at least one participant'));
    }

    const draftId = `draft-group-${Date.now()}`;

    const participantObjects = memberIds
      .map((memberId) => userLookup[memberId] ?? employees.find((emp) => emp?.id === memberId))
      .filter(Boolean);

    if (currentUserProfile) {
      participantObjects.push(currentUserProfile);
    }

    const draftConversation = {
      id: draftId,
      type: 'group',
      name: groupData?.name ?? translate('defaults.newGroupName', 'New Group'),
      participants: participantObjects,
      metadata: groupData?.description ? { description: groupData.description } : undefined,
      isDraft: true,
      draftPayload: {
        type: 'group',
        name: groupData?.name ?? translate('defaults.newGroupName', 'New Group'),
        participants: memberIds,
        metadata: groupData?.description ? { description: groupData.description } : undefined,
      },
      lastMessageAt: new Date().toISOString(),
    };

    setRawConversations((prev) => [draftConversation, ...prev]);
    setMessagesByConversation((prev) => ({
      ...prev,
      [draftId]: [],
    }));
    setSelectedConversationId(draftId);

    return draftConversation;
  }, [currentUserProfile, employees, translate, userLookup]);

  useEffect(() => {
    if (selectedConversation?.id === 'temp-typing-sim') {
      return;
    }

    /* 
    TODO: support this later
    if (selectedConversation?.id) {
      const timer = setTimeout(() => {
        setTypingUsers(selectedConversation?.id === enhancedConversations[0]?.id ? ['Sarah Johnson'] : []);
        setTimeout(() => setTypingUsers([]), 3000);
      }, 2000);
      return () => clearTimeout(timer);
    } */
  }, [selectedConversation, enhancedConversations]);

  const headerLocationName = useMemo(() => {
    if (Array.isArray(locations) && locations.length > 0) {
      return locations[0]?.name ?? '';
    }
    if (locationsLoading) {
      return t('header.location.loadingLocations');
    }
    return '';
  }, [locations, locationsLoading, t]);

  const departments = useMemo(
    () =>
      Array.from(
        employees.reduce((acc, employee) => {
          if (employee?.department) {
            acc.add(employee.department);
          }
          return acc;
        }, new Set())
      ).map((dept) => ({ id: dept, name: dept })),
    [employees]
  );

  const headerNotifications = useMemo(
    () => ({
      messages: enhancedConversations.reduce(
        (total, conversation) =>
          total + (conversation?.unseenCount ?? conversation?.unreadCount ?? 0),
        0
      ),
      timeOff: 0,
    }),
    [enhancedConversations]
  );

  const groupChatEmployees = useMemo(
    () =>
      employees.map((employee) => ({
        id: employee?.id,
        name: employee?.name,
        department: employee?.department,
        role: employee?.role,
        avatarFileId: employee?.avatarFileId ?? null,
        avatarUrl: employee?.avatarUrl ?? null,
        isOnline: false,
      })),
    [employees]
  );

  const currentEmployeeIdentifiers = useMemo(() => {
    if (!currentUserIdentifiers.length) {
      return [];
    }

    const userIdSet = new Set(currentUserIdentifiers);
    const employeeIdentifiers = new Set();
    const registerEmployee = (value) => {
      const normalized = normalizeIdentifier(value);
      if (normalized) {
        employeeIdentifiers.add(normalized);
      }
    };

    employees.forEach((employee) => {
      const candidateIds = new Set();
      const collectCandidate = (value) => {
        const normalized = normalizeIdentifier(value);
        if (normalized) {
          candidateIds.add(normalized);
        }
      };

      collectCandidate(employee?.userId);
      collectCandidate(employee?.raw?.userId);
      collectCandidate(employee?.raw?.user);
      collectCandidate(employee?.raw?.user?.id);
      collectCandidate(employee?.raw?.user?._id);
      collectCandidate(employee?.raw?.user?.userId);
      collectCandidate(employee?.raw?.user?.user_id);
      collectCandidate(employee?.raw?.user?.sub);

      const matchesCurrentUser = Array.from(candidateIds).some((candidate) =>
        userIdSet.has(candidate)
      );

      if (matchesCurrentUser) {
        registerEmployee(employee?.id);
        registerEmployee(employee?.employeeId);
        registerEmployee(employee?.employee_id);
        registerEmployee(employee?.raw?.id);
        registerEmployee(employee?.raw?.employeeId);
        registerEmployee(employee?.raw?.employee_id);
      }
    });

    return Array.from(employeeIdentifiers);
  }, [employees, currentUserIdentifiers]);

  const directMessageOptions = useMemo(() => {
    const currentUserIdSet = new Set(currentUserIdentifiers);
    const currentEmployeeIdSet = new Set(currentEmployeeIdentifiers);
    const seenOptionIds = new Set();
    const seenUserIdentifiers = new Set();
    const options = [];

    const employeeCandidates = directMessageQuery?.trim()
      ? employeeSearchResults
      : employees;

    const appendOption = (id, details = {}) => {
      const candidateUserIds = new Set();
      const candidateEmployeeIds = new Set();
      const registerUserCandidate = (value) => {
        const normalized = normalizeIdentifier(value);
        if (normalized) {
          candidateUserIds.add(normalized);
        }
      };
      const registerEmployeeCandidate = (value) => {
        const normalized = normalizeIdentifier(value);
        if (normalized) {
          candidateEmployeeIds.add(normalized);
        }
      };

      registerUserCandidate(id);
      registerUserCandidate(details?.userId);
      registerEmployeeCandidate(details?.employeeId);

      const rawEmployee = details?.rawEmployee ?? null;
      if (rawEmployee) {
        registerEmployeeCandidate(rawEmployee?.id);
        registerEmployeeCandidate(rawEmployee?.employeeId);
        registerEmployeeCandidate(rawEmployee?.employee_id);
        registerEmployeeCandidate(rawEmployee?.raw?.id);
        registerEmployeeCandidate(rawEmployee?.raw?.employeeId);
        registerEmployeeCandidate(rawEmployee?.raw?.employee_id);

        registerUserCandidate(rawEmployee?.userId);
        registerUserCandidate(rawEmployee?.user_id);
        registerUserCandidate(rawEmployee?.user);
        registerUserCandidate(rawEmployee?.raw?.userId);
        registerUserCandidate(rawEmployee?.raw?.user);
        registerUserCandidate(rawEmployee?.raw?.user?.id);
        registerUserCandidate(rawEmployee?.raw?.user?._id);
        registerUserCandidate(rawEmployee?.raw?.user?.userId);
        registerUserCandidate(rawEmployee?.raw?.user?.user_id);
        registerUserCandidate(rawEmployee?.raw?.user?.sub);
      }

      const rawParticipant = details?.rawParticipant ?? null;
      if (rawParticipant) {
        registerUserCandidate(rawParticipant);
        registerUserCandidate(rawParticipant?.id);
        registerUserCandidate(rawParticipant?._id);
        registerUserCandidate(rawParticipant?.userId);
        registerUserCandidate(rawParticipant?.user_id);
        registerUserCandidate(rawParticipant?.sub);
      }

      const normalizedId = normalizeIdentifier(id);
      const derivedId = normalizedId
        ?? Array.from(candidateUserIds)[0]
        ?? null;

      if (!derivedId) {
        return;
      }

      candidateUserIds.add(derivedId);

      const matchesCurrentUser = (
        Array.from(candidateUserIds).some((candidate) =>
          currentUserIdSet.has(candidate)
        )
        || Array.from(candidateEmployeeIds).some((candidate) =>
          currentEmployeeIdSet.has(candidate)
        )
      );

      if (matchesCurrentUser) {
        return;
      }

      if (seenOptionIds.has(derivedId)) {
        return;
      }

      const hasDuplicateUser = Array.from(candidateUserIds).some((candidate) =>
        seenUserIdentifiers.has(candidate)
      );
      if (hasDuplicateUser) {
        return;
      }

      const lookupEntry =
        userLookup[derivedId]
          ?? Array.from(candidateUserIds)
            .map((candidate) => userLookup[candidate])
            .find(Boolean)
          ?? {};

      const nameCandidates = collectStringCandidates(
        details?.name,
        details?.fullName,
        details?.label,
        lookupEntry?.name,
        rawEmployee,
        rawEmployee?.raw,
        rawEmployee?.user,
        rawEmployee?.raw?.user,
        details?.rawParticipant,
      );

      const optionName =
        nameCandidates[0]
        ?? lookupEntry?.name
        ?? details?.email
        ?? lookupEntry?.email
        ?? `User ${String(derivedId).slice(-4)}`;

      const optionEmail =
        details?.email
        ?? lookupEntry?.email
        ?? rawEmployee?.email
        ?? rawEmployee?.raw?.email
        ?? details?.rawParticipant?.email
        ?? '';

      const optionAvatarUrl =
        details?.avatarUrl
        ?? (typeof lookupEntry?.avatarUrl === 'string'
          ? lookupEntry.avatarUrl
          : null)
        ?? (typeof details?.avatar === 'string' ? details.avatar : null)
        ?? rawEmployee?.avatarUrl
        ?? rawEmployee?.raw?.avatarUrl
        ?? details?.rawParticipant?.avatarUrl
        ?? details?.rawParticipant?.avatar
        ?? null;

      const optionDepartment =
        details?.department
        ?? rawEmployee?.department
        ?? rawEmployee?.raw?.department
        ?? details?.rawParticipant?.department
        ?? null;

      const optionRole =
        details?.role
        ?? rawEmployee?.role
        ?? rawEmployee?.raw?.role
        ?? details?.rawParticipant?.role
        ?? null;

      let description = details?.description ?? null;
      if (!description) {
        if (optionDepartment && optionRole) {
          description = `${optionDepartment} • ${optionRole}`;
        } else if (optionDepartment) {
          description = optionDepartment;
        } else if (optionRole) {
          description = optionRole;
        } else if (optionEmail) {
          description = optionEmail;
        }
      }

      const searchTerms = collectSearchTerms(
        optionName,
        nameCandidates,
        optionEmail,
        optionDepartment,
        optionRole,
        rawEmployee,
        rawEmployee?.raw,
        rawEmployee?.user,
        rawEmployee?.raw?.user,
        details?.rawParticipant,
      );

      options.push({
        id: derivedId,
        name: optionName,
        email: optionEmail,
        avatarUrl: optionAvatarUrl,
        description,
        department: optionDepartment,
        role: optionRole,
        searchText: searchTerms
          .map((term) => term.toLowerCase())
          .join(' '),
        searchTerms,
      });

      seenOptionIds.add(derivedId);
      Array.from(candidateUserIds).forEach((candidate) => {
        seenUserIdentifiers.add(candidate);
      });
    };

    employeeCandidates.forEach((employee) => {
      const userReference = employee?.raw?.user;
      const employeeUserId =
        employee?.userId
        ?? employee?.user_id
        ?? (typeof userReference === 'object'
          ? userReference?.id ?? userReference?._id
          : userReference)
        ?? employee?.id
        ?? employee?.employeeId
        ?? employee?.employee_id;

      appendOption(employeeUserId, {
        name: employee?.name ?? employee?.fullName,
        email: employee?.email,
        avatarUrl: employee?.avatarUrl ?? null,
        department: employee?.department,
        role: employee?.role,
        rawEmployee: employee,
      });
    });

    users.forEach((userDoc) => {
      const userId = userDoc?.id ?? userDoc?._id;
      appendOption(userId, {
        name: userDoc?.fullName,
        email: userDoc?.email,
        avatarUrl: userDoc?.avatarUrl,
        role: userDoc?.role,
      });
    });

    rawConversations.forEach((conversation) => {
      (conversation?.participants ?? []).forEach((participant) => {
        const participantId = resolveParticipantId(participant);
        const participantObject =
          participant && typeof participant === 'object' ? participant : {};
        appendOption(participantId, {
          name: participantObject?.fullName ?? participantObject?.name,
          email: participantObject?.email,
          avatarUrl: participantObject?.avatarUrl ?? participantObject?.avatar,
          department: participantObject?.department,
          role: participantObject?.role,
          rawParticipant: participantObject,
        });
      });
    });

    return options.sort((a, b) => {
      const nameA = (a?.name ?? '').toLowerCase();
      const nameB = (b?.name ?? '').toLowerCase();
      if (nameA && nameB) {
        return nameA.localeCompare(nameB);
      }
      if (nameA) {
        return -1;
      }
      if (nameB) {
        return 1;
      }
      return 0;
    });
  }, [
    currentEmployeeIdentifiers,
    currentUserIdentifiers,
    directMessageQuery,
    employeeSearchResults,
    employees,
    rawConversations,
    resolveParticipantId,
    userLookup,
    users,
  ]);

  const handleSearchChange = useCallback(
    (value) => {
      setSearchQuery(value);
    },
    [setSearchQuery]
  );

  const handleDirectMessageQueryChange = useCallback(
    (value) => {
      setDirectMessageQuery(value);
      if (directMessageError) {
        setDirectMessageError(null);
      }
    },
    [directMessageError]
  );

  const overallLoading = loading || locationsLoading;
  const combinedError = error ?? locationsError;

  const retry = useCallback(() => {
    const controller = { current: true };
    fetchAllData(controller);
    if (locationsError) {
      refetchLocations().catch(() => {});
    }
  }, [fetchAllData, locationsError, refetchLocations]);

  const handleSelectDirectRecipient = useCallback(
    async (recipient) => {
      const recipientId = recipient?.id ?? recipient?._id;
      if (!recipientId) {
        return;
      }

      const normalizedRecipientId = String(recipientId);
      setDirectMessageError(null);

      const existingConversation = enhancedConversations.find(
        (conversation) => {
          if ((conversation?.type ?? 'direct') !== 'direct') {
            return false;
          }

          const participantIds = (conversation?.participants ?? [])
            .map((participant) => resolveParticipantId(participant))
            .filter(Boolean);

          if (!participantIds.includes(normalizedRecipientId)) {
            return false;
          }

          if (user?.sub) {
            return participantIds.includes(String(user.sub));
          }

          return true;
        }
      );

      if (existingConversation?.id) {
        setSelectedConversationId(existingConversation.id);
        if (isMobileView) {
          setIsConversationListVisible(false);
        }
        setDirectMessageQuery('');
        return;
      }

      setIsCreatingDirectConversation(true);
      try {
        const draftId = `draft-${normalizedRecipientId}-${Date.now()}`;
        const draftConversation = {
          id: draftId,
          type: 'direct',
          participants: [recipient, currentUserProfile].filter(Boolean),
          isDraft: true,
          draftPayload: {
            type: 'direct',
            participants: [normalizedRecipientId],
          },
          lastMessageAt: new Date().toISOString(),
        };

        setRawConversations((prev) => [draftConversation, ...prev]);
        setMessagesByConversation((prev) => ({
          ...prev,
          [draftId]: prev[draftId] ?? [],
        }));

        setSelectedConversationId(draftId);
        if (isMobileView) {
          setIsConversationListVisible(false);
        }
        setDirectMessageQuery('');
      } catch (err) {
        const responseMessage = err?.response?.data?.message;
        const message = Array.isArray(responseMessage)
          ? responseMessage.join('. ')
          : responseMessage
            ?? err?.message
            ?? translate('errors.conversation.startFailed', 'Unable to start conversation.');
        setDirectMessageError(message);
      } finally {
        setIsCreatingDirectConversation(false);
      }
    },
    [
      enhancedConversations,
      isMobileView,
      resolveParticipantId,
      currentUserProfile,
      setIsConversationListVisible,
      translate,
      user?.sub,
    ]
  );

  const handleSelectDirectRecipientRef = useRef(handleSelectDirectRecipient);

  useEffect(() => {
    handleSelectDirectRecipientRef.current = handleSelectDirectRecipient;
  }, [handleSelectDirectRecipient]);

  useEffect(() => {
    const state = location.state ?? {};
    const params = new URLSearchParams(location.search ?? '');

    const rawRecipient =
      state?.directRecipientId ??
      state?.recipientId ??
      state?.directRecipient ??
      params.get('recipient') ??
      params.get('userId') ??
      params.get('employeeId');

    const normalizedRecipientId = normalizeIdentifier(rawRecipient);

    if (!normalizedRecipientId) {
      return;
    }

    const recipientPayload = {
      id: normalizedRecipientId,
      name:
        state?.directRecipientName ??
        state?.recipientName ??
        params.get('name'),
      email:
        state?.directRecipientEmail ??
        state?.recipientEmail ??
        params.get('email'),
    };

    let cancelled = false;

    const cleanupState = () => {
      if (cancelled) {
        return;
      }

      const nextState = { ...state };
      let mutated = false;
      [
        'directRecipientId',
        'directRecipientName',
        'directRecipientEmail',
        'directRecipient',
        'recipientId',
        'recipientName',
        'recipientEmail',
      ].forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(nextState, key)) {
          delete nextState[key];
          mutated = true;
        }
      });

      if (!mutated) {
        return;
      }

      const remainingKeys = Object.keys(nextState);
      navigate(location.pathname + location.search, {
        replace: true,
        state: remainingKeys.length ? nextState : null,
      });
    };

    const execute = async () => {
      try {
        await handleSelectDirectRecipientRef.current(recipientPayload);
      } finally {
        cleanupState();
      }
    };

    execute();

    return () => {
      cancelled = true;
    };
  }, [location.key, location.pathname, location.search, location.state, navigate]);

  if (overallLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          userRole={userRole}
          selectedLocation={headerLocationName}
          locations={locations}
          onLocationChange={() => {}}
          notifications={headerNotifications}
        />
        <div className="pt-16">
          <LoadingState
            message={translate('page.loading', 'Loading conversations…')}
            heightClassName="min-h-[calc(100vh-4rem)]"
          />
        </div>
      </div>
    );
  }

  if (combinedError) {
    const isLocationFetchError = !error && !!locationsError;
    const apiErrorMessage = Array.isArray(
      combinedError?.response?.data?.message
    )
      ? combinedError.response.data.message.join('. ')
      : combinedError?.response?.data?.message || combinedError?.message;
    const errorMessage = isLocationFetchError
      ? apiErrorMessage || translate('errors.locations', 'Unable to load locations.')
      : apiErrorMessage;

    return (
      <div className="min-h-screen bg-background">
        <Header
          userRole={userRole}
          selectedLocation={headerLocationName}
          locations={locations}
          onLocationChange={() => {}}
          notifications={headerNotifications}
        />
        <div className="pt-16 flex flex-col items-center justify-center text-muted-foreground space-y-4">
          <p>{translate('page.error.message', 'Unable to load messaging data.')}</p>
          {errorMessage && (
            <p className="text-sm text-muted-foreground/80 max-w-md text-center">
              {errorMessage}
            </p>
          )}
          <Button variant="outline" onClick={retry}>
            {translate('page.error.retry', 'Try again')}
          </Button>
        </div>
      </div>
    );
  }

  const modalDepartments = departments.length
    ? departments
    : [
        {
          id: 'general',
          name: translate('defaults.generalDepartment', 'General'),
        },
      ];
  const canCreateAnnouncements = ['manager', 'admin'].includes(userRole);

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole={userRole}
        selectedLocation={headerLocationName}
        locations={locations}
        onLocationChange={() => {}}
        notifications={headerNotifications}
      />

      <div className="pt-16 h-screen flex flex-col lg:flex-row">
        <ConversationList
          conversations={enhancedConversations}
          selectedConversation={selectedConversation}
          onSelectConversation={handleSelectConversation}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          directMessageQuery={directMessageQuery}
          onDirectMessageQueryChange={handleDirectMessageQueryChange}
          directMessageOptions={directMessageOptions}
          onSelectDirectRecipient={handleSelectDirectRecipient}
          isCreatingDirectConversation={isCreatingDirectConversation}
          directMessageError={directMessageError}
          isSearchingDirectMessage={employeeSearchLoading}
          directMessageSearchError={employeeSearchError}
          onOpenGroupChat={() => setShowGroupChatModal(true)}
          onOpenAnnouncement={() => setShowAnnouncementModal(true)}
          canCreateAnnouncement={canCreateAnnouncements}
          className={
            isMobileView && !isConversationListVisible ? 'hidden lg:flex' : ''
          }
        />

        <MessagePanel
          conversation={selectedConversation}
          messages={conversationMessages}
          onSendMessage={handleSendMessage}
          currentUser={currentUserProfile}
          typingUsers={typingUsers}
          className={
            isMobileView && isConversationListVisible ? 'hidden lg:flex' : ''
          }
          onBack={handleBackToConversationList}
          showBackButton={isMobileView}
          onDeleteConversation={handleDeleteConversation}
        />
      </div>

      <AnnouncementModal
        isOpen={showAnnouncementModal}
        onClose={() => setShowAnnouncementModal(false)}
        onSend={handleSendAnnouncement}
        locations={locations}
        departments={modalDepartments}
      />

      <GroupChatModal
        isOpen={showGroupChatModal}
        onClose={() => setShowGroupChatModal(false)}
        onCreate={handleCreateGroup}
        employees={groupChatEmployees}
      />
    </div>
  );
};

export default InternalMessaging;
